import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Eye, BarChart3, Brain, Settings, RefreshCw, Rocket } from 'lucide-react';
interface SlideFlywheelProps {
  onContinue: () => void;
}
export function SlideFlywheel({
  onContinue
}: SlideFlywheelProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 1000); // rings & center
    const t3 = setTimeout(() => setStage(3), 1700); // satellites pop in
    const t4 = setTimeout(() => setStage(4), 3000); // start orbiting
    const t5 = setTimeout(() => setStage(5), 3400); // CTA
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, []);
  // Satellites positioned around the orbit at evenly-spaced angles (60° apart).
  // Angle measured clockwise from 12 o'clock (top).
  const satellites = [{
    angle: -30,
    icon: <Eye className="w-5 h-5" strokeWidth={2.2} data-id="element-353" />,
    label: 'Engage',
    delay: 0
  }, {
    angle: 30,
    icon: <BarChart3 className="w-5 h-5" strokeWidth={2.2} data-id="element-354" />,
    label: 'Measure',
    delay: 0.1
  }, {
    angle: 90,
    icon: <Brain className="w-5 h-5" strokeWidth={2.2} data-id="element-355" />,
    label: 'Learn',
    delay: 0.2
  }, {
    angle: 150,
    icon: <Settings className="w-5 h-5" strokeWidth={2.2} data-id="element-356" />,
    label: 'Optimize',
    delay: 0.3
  }, {
    angle: 210,
    icon: <RefreshCw className="w-5 h-5" strokeWidth={2.2} data-id="element-357" />,
    label: 'Relaunch',
    delay: 0.4
  }, {
    angle: 270,
    icon: <Rocket className="w-5 h-5" strokeWidth={2.2} data-id="element-358" />,
    label: 'Launch',
    delay: 0.5
  }];
  // Orbit radius (px) — distance from center to satellite icon
  const radius = 200;
  // Slight stagger to suggest "two orbital rings" — alternate ±18px
  const stagger = (i: number) => i % 2 === 0 ? 0 : -18;
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-359">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-360">
        <h2 className="text-4xl md:text-6xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-361">
          The flywheel
        </h2>
        <motion.div initial={{
        width: 0
      }} animate={{
        width: stage >= 1 ? 80 : 0
      }} transition={{
        duration: 0.5
      }} className="h-[3px] bg-blue-400 rounded-full mt-3" data-id="element-362" />
      </motion.div>

      {/* Flywheel canvas */}
      <div className="flex-1 flex items-center justify-center" data-id="element-363">
        <div className="relative" style={{
        width: 560,
        height: 560
      }} data-id="element-364">
          {/* Soft ambient glow behind the center */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-365">
            <div className="w-72 h-72 bg-blue-400/10 rounded-full blur-3xl" data-id="element-366" />
          </div>

          {/* Orbital rings */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          scale: stage >= 2 ? 1 : 0.85
        }} transition={{
          duration: 0.8
        }} className="absolute inset-0" data-id="element-367">
            <svg viewBox="0 0 560 560" className="w-full h-full" data-id="element-368">
              <circle cx="280" cy="280" r="200" fill="none" stroke="#3A5278" strokeOpacity="0.35" strokeWidth="1" data-id="element-369" />
              <circle cx="280" cy="280" r="218" fill="none" stroke="#3A5278" strokeOpacity="0.2" strokeWidth="1" strokeDasharray="2,5" data-id="element-370" />
            </svg>
          </motion.div>

          {/* Center bubble with the headline */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.7
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          scale: stage >= 2 ? 1 : 0.7
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="absolute inset-0 flex items-center justify-center" data-id="element-371">
            <div className="w-56 h-56 rounded-full bg-navy-900/90 border border-navy-600 flex items-center justify-center text-center px-6 shadow-[0_0_40px_rgba(96,165,250,0.1)]" data-id="element-372">
              <p className="text-xl font-bold leading-snug bg-gradient-to-r from-blue-400 via-teal-300 to-teal-400 bg-clip-text text-transparent" data-id="element-373">
                Every campaign
                <br data-id="element-374" />
                makes the next
                <br data-id="element-375" />
                one smarter.
              </p>
            </div>
          </motion.div>

          {/* Rotating orbit container */}
          <motion.div className="absolute inset-0" animate={stage >= 4 ? {
          rotate: 360
        } : {
          rotate: 0
        }} transition={stage >= 4 ? {
          duration: 60,
          repeat: Infinity,
          ease: 'linear'
        } : {
          duration: 0
        }} data-id="element-376">
            {satellites.map((s, idx) => {
            const r = radius + stagger(idx);
            const rad = (s.angle - 90) * Math.PI / 180; // convert to standard math (0 = right) and rotate so 0° is up
            const x = 280 + r * Math.cos(rad);
            const y = 280 + r * Math.sin(rad);
            return <motion.div key={s.label} initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              scale: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 0.5,
              delay: stage >= 3 ? s.delay : 0
            }} className="absolute" style={{
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)'
            }} data-id="element-377">
                  {/* Counter-rotate so satellites stay upright while the orbit container spins */}
                  <motion.div animate={stage >= 4 ? {
                rotate: -360
              } : {
                rotate: 0
              }} transition={stage >= 4 ? {
                duration: 60,
                repeat: Infinity,
                ease: 'linear'
              } : {
                duration: 0
              }} className="flex flex-col items-center gap-2" data-id="element-378">
                    <div className="w-12 h-12 rounded-full bg-navy-700/80 border border-navy-500 flex items-center justify-center text-white/70 backdrop-blur-sm shadow-lg" data-id="element-379">
                      {s.icon}
                    </div>
                    <span className="px-2.5 py-0.5 bg-navy-900/80 text-white/70 text-xs font-medium rounded-full border border-navy-600" data-id="element-380">
                      {s.label}
                    </span>
                  </motion.div>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 5 ? 1 : 0
    }} className="mt-4 flex justify-end" data-id="element-381">
        <Button onClick={onContinue} data-id="element-382">Platform Architecture</Button>
      </motion.div>
    </div>;
}